import { PaginationProps, TableProps } from 'antd';
import { deleteApi, getApi } from 'apis';
import PopUpWrapper from 'components/Global/PopUpWrapper';
import TableActionHeader from 'components/Global/TableActionHeader';
import ViewPopUpWrapper from 'components/Global/ViewPopUpWrapper';
import ConfirmDelete from 'components/Global/confirmDeleteModel';
import { Dayjs } from 'dayjs';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { toastText } from 'utils/utils';
import AddEditComboBody from './AddEditComboBody';
import CustomTable from './CustomTable';
import ViewComboBody from './ViewComboBody';
import { useDebounce } from 'utils/hooks';
import {
  ComboCategoryOption,
  StatusFilterOption,
} from './SearchAndFilter/types';

export const CombosTable = () => {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(
    Number(localStorage.getItem('comboTableSize')) || 10,
  );
  const [sortField, setSortField] = useState('createdAt');
  const [sortType, setSortType] = useState('desc');
  const [selectedItem, setSelectedItem] = useState<any>();
  const [editSelectedCombo, setEditSelectedCombo] = useState<any>();
  const [title, setTitle] = useState('Add Combo');
  const [totalItems, setTotalItems] = useState(0);
  const [comboData, setComboData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isPoppedUp, setIsPoppedUp] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<StatusFilterOption | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [subCategoryFilter, setSubCategoryFilter] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<[Dayjs, Dayjs] | null>(null);
  const [categories, setCategories] = useState<ComboCategoryOption[]>([]);

  const debouncedSearch = useDebounce(search, 500);

  const viewHandler = (item: any) => {
    setSelectedItem(item);
    setIsViewOpen(true);
  };

  const openDrawerHandler = () => {
    setTitle('Add Combo');
    setEditSelectedCombo(null);
    setIsPoppedUp(true);
  };

  const closeDrawerByAnimation = () => {
    setIsPoppedUp(false);
    setEditSelectedCombo(null);
  };

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setEditSelectedCombo(null);
  };

  const handleOk = () => {
    setIsModalOpen(false);
    setEditSelectedCombo(null);
  };

  const deleteHandler = async () => {
    if (!editSelectedCombo?.id) return;
    try {
      setIsDeleteLoading(true);
      const resp = await deleteApi(`/combo/delete/${editSelectedCombo.id}`);
      toastText(
        resp?.data?.message ?? 'Combo item deleted successfully',
        'success',
      );
    } catch (error: any) {
      toastText(
        error?.response?.data?.message ??
          'Something went wrong while deleting combo item',
        'error',
      );
    } finally {
      setIsDeleteLoading(false);
      handleCancel();
      fetchCombos();
    }
  };

  const performSearchHandler = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, categoryFilter, subCategoryFilter, selectedDate]);

  const fetchCombos = useCallback(async () => {
    setIsLoading(true);
    const query: Record<string, unknown> = {
      page,
      limit,
      search: debouncedSearch,
      sorterField: sortField,
      type: sortType,
      targetField: 'comboName',
      selectedDate,
    };
    if (statusFilter) query.status = statusFilter;
    if (categoryFilter) query.categoryId = categoryFilter;
    if (subCategoryFilter) query.subCategoryId = subCategoryFilter;

    try {
      const res = await getApi('/combo/get-all', query);
      setComboData(res?.data?.data || []);
      setTotalItems(res?.data?.total || 0);
    } catch (error: any) {
      toastText(
        error?.response?.data?.error?.errorDescription ||
          'Failed to fetch combo items',
        'error',
      );
    } finally {
      setIsLoading(false);
    }
  }, [
    page,
    limit,
    debouncedSearch,
    sortField,
    sortType,
    statusFilter,
    categoryFilter,
    subCategoryFilter,
    selectedDate,
  ]);

  const fetchCategories = async () => {
    try {
      const res = await getApi('/combo-category/get-all');
      if (res?.data?.data) {
        setCategories(
          res.data.data.map(
            (c: {
              id: string;
              name: string;
              subCategories?: { id: string; name: string }[];
            }) => ({
              id: c.id,
              name: c.name,
              subCategories: c.subCategories || [],
            }),
          ),
        );
      }
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    fetchCombos();
  }, [fetchCombos]);

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleTableChange: TableProps<any>['onChange'] = (_pagination, _filters, sorter) => {
    const sortOrder = Array.isArray(sorter)
      ? sorter[0]?.order
      : sorter.order === 'ascend'
        ? 'asc'
        : 'desc';
    setSortType(sortOrder || 'asc');

    const field = Array.isArray(sorter) ? sorter[0]?.field : sorter.field;
    setSortField((field as string) || 'comboName');
  };

  const onShowSizeChange: PaginationProps['onShowSizeChange'] = (current, pageSize) => {
    setPage(current);
    setLimit(pageSize);
    localStorage.setItem('comboTableSize', String(pageSize));
  };

  return (
    <>
      <div>
        <TableActionHeader title="Combos" />
        <CustomTable
          dataSource={comboData}
          handleTableChange={handleTableChange}
          onShowSizeChange={onShowSizeChange}
          isLoading={isLoading}
          search={search}
          totalRecords={totalItems}
          performSearchHandler={performSearchHandler}
          currentPage={page}
          pageSize={limit}
          setSearch={setSearch}
          viewHandler={viewHandler}
          openDrawerHandler={openDrawerHandler}
          setEditSelectedCombo={setEditSelectedCombo}
          setTitle={setTitle}
          showModal={showModal}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          categoryFilter={categoryFilter}
          onCategoryFilterChange={setCategoryFilter}
          subCategoryFilter={subCategoryFilter}
          onSubCategoryFilterChange={setSubCategoryFilter}
          categories={categories}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
        />
      </div>

      <ConfirmDelete
        handleCancel={handleCancel}
        handleOk={handleOk}
        isModalOpen={isModalOpen}
        deleteHandler={deleteHandler}
        isLoading={isDeleteLoading}
        selectedDeleteData={editSelectedCombo?.comboName}
        deleteHeading="Combo"
      />

      {isPoppedUp && (
        <PopUpWrapper
          title={title}
          isPoppedUp={isPoppedUp}
          closeDrawerByAnimation={closeDrawerByAnimation}
        >
          <AddEditComboBody
            closeDrawerByAnimation={closeDrawerByAnimation}
            editSelectedCombo={editSelectedCombo}
            setEditSelectedCombo={setEditSelectedCombo}
            fetchCombos={fetchCombos}
          />
        </PopUpWrapper>
      )}

      {isViewOpen && (
        <ViewPopUpWrapper
          title="Combo Details"
          isPoppedUp={isViewOpen}
          closeDrawerByAnimation={() => {
            setIsViewOpen(false);
            setSelectedItem(null);
          }}
        >
          <ViewComboBody combo={selectedItem} />
        </ViewPopUpWrapper>
      )}
    </>
  );
};

export default CombosTable;
